import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, PrimaryButton, Modal, Field, TextInput, Select, Textarea, useFetch, StatusPill } from "../../components/crm/ui";
import { Plus, Trash2, Edit, Percent, IndianRupee } from "lucide-react";

const EMPTY = {
  name: "", description: "", min_value: 0, max_value: 0,
  rate_type: "percentage", rate: 0, applicable_customer_type: "all", status: "active",
};

export default function IncentiveSlabs() {
  const { data: slabs, reload } = useFetch("/incentive-slabs");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  function startCreate() { setForm(EMPTY); setEditingId(null); setOpen(true); }
  function startEdit(s) {
    setForm({ ...EMPTY, ...s });
    setEditingId(s.id);
    setOpen(true);
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        min_value: Number(form.min_value) || 0,
        max_value: Number(form.max_value) || 0,
        rate: Number(form.rate) || 0,
      };
      if (editingId) {
        await api.put(`/incentive-slabs/${editingId}`, payload);
      } else {
        await api.post("/incentive-slabs", payload);
      }
      toast.success(editingId ? "Updated" : "Slab created");
      setOpen(false); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  }

  async function remove(s) {
    if (!window.confirm(`Delete slab ${s.name}?`)) return;
    await api.delete(`/incentive-slabs/${s.id}`);
    toast.success("Deleted");
    reload();
  }

  return (
    <div data-testid="slabs-page">
      <PageHeader
        overline="Master data"
        title="SC Incentive Slabs"
        action={<PrimaryButton onClick={startCreate} testid="create-slab-btn"><Plus className="w-4 h-4" /> New Slab</PrimaryButton>}
      />

      <div className="bg-white border border-[#DDE5D8]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="slabs-table">
            <thead className="bg-[#F4F6F1] border-b border-[#DDE5D8]">
              <tr>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Name</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Value range</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Rate</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Customer type</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Status</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {slabs.map((s) => (
                <tr key={s.id} data-testid={`slab-row-${s.id}`} className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1]">
                  <td className="px-4 py-3">
                    <div className="font-display font-semibold">{s.name}</div>
                    {s.description && <div className="font-mono text-[10px] text-[#94A3B8] mt-0.5">{s.description}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    ₹{Number(s.min_value).toLocaleString("en-IN")}
                    {s.max_value > 0 ? ` – ₹${Number(s.max_value).toLocaleString("en-IN")}` : "+"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-[#1F4E2C] flex items-center justify-end gap-0.5">
                    {s.rate_type === "percentage" ? <Percent className="w-3 h-3" /> : <IndianRupee className="w-3 h-3" />}
                    {s.rate}{s.rate_type === "percentage" ? "%" : ""}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{(s.applicable_customer_type || "all").replace("_", " ")}</td>
                  <td className="px-4 py-3"><StatusPill value={s.status} tone={s.status === "active" ? "success" : "muted"} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => startEdit(s)} data-testid={`edit-${s.id}`} className="p-1 text-[#475569] hover:text-[#1F4E2C]"><Edit className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(s)} data-testid={`del-${s.id}`} className="p-1 text-[#475569] hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {slabs.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-sm font-mono text-[#94A3B8]">No incentive slabs yet. Create one to publish to your SCs.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editingId ? "Edit Slab" : "New Slab"} wide>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="slab-form">
          <Field label="Name *"><TextInput required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" /></Field>
          <Field label="Status">
            <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </Field>
          <Field label="Description" className="md:col-span-2"><TextInput value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label="Min order value (₹)"><TextInput type="number" value={form.min_value} onChange={(e) => setForm({ ...form, min_value: e.target.value })} /></Field>
          <Field label="Max value (0 = no cap)"><TextInput type="number" value={form.max_value} onChange={(e) => setForm({ ...form, max_value: e.target.value })} /></Field>
          <Field label="Rate type">
            <Select value={form.rate_type} onChange={(e) => setForm({ ...form, rate_type: e.target.value })}>
              <option value="percentage">Percentage of order</option>
              <option value="flat">Flat amount (₹)</option>
            </Select>
          </Field>
          <Field label={form.rate_type === "percentage" ? "Rate (%)" : "Flat ₹ amount"}><TextInput type="number" required value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} data-testid="form-rate" /></Field>
          <Field label="Applicable customer type">
            <Select value={form.applicable_customer_type} onChange={(e) => setForm({ ...form, applicable_customer_type: e.target.value })}>
              <option value="all">All</option>
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
              <option value="housing_society">Housing Society</option>
            </Select>
          </Field>
          <div className="md:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#475569]">Cancel</button>
            <PrimaryButton type="submit" disabled={saving} testid="save-slab-btn">{saving ? "Saving…" : (editingId ? "Update" : "Create")}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
